import type { AreaPosition } from "@corklog/shared";

/**
 * The arithmetic behind an area report's map — task 10.35, decided
 * 2026-09-28 in `project-scope.md`: **points on a graticule, never borders
 * and never tiles.** The Champagne preview drew it this way and the owner
 * chose it over a zoomable map from a tile provider.
 *
 * Pure, so the drawing in `AreaMap.tsx` has nothing to decide and this can be
 * tested without a browser.
 *
 * **The projection is equirectangular with the longitude squeezed by the
 * cosine of the middle latitude**, which is what a map of one wine area needs
 * and no more: across a hundred and fifty kilometres the error against a
 * proper projection is smaller than the error in the model's own positions,
 * and a north-up map with true proportions at its centre is what a reader
 * expects. A projection library would be a dependency for a difference
 * nobody could see.
 */

/** A point placed on the drawing, with its label. */
export type PlacedPoint = {
  readonly x: number;
  readonly y: number;
  readonly label: string;
  /** Where the label goes relative to the point. */
  readonly anchor: "start" | "end";
  readonly labelX: number;
  readonly labelY: number;
};

export type GridLine = { readonly at: number; readonly label: string };

export type MapLayout = {
  readonly width: number;
  readonly height: number;
  readonly points: readonly PlacedPoint[];
  /** Horizontal lines: y on the drawing, and the latitude they stand for. */
  readonly latitudes: readonly GridLine[];
  /** Vertical lines: x on the drawing, and the longitude. */
  readonly longitudes: readonly GridLine[];
  /** A bar of a round number of kilometres, and its length on the drawing. */
  readonly scale: { readonly km: number; readonly length: number };
  /** Degrees to the drawing — the one projection, so the outlines under the
   *  points are drawn by exactly the arithmetic that placed them. */
  readonly project: (lng: number, lat: number) => readonly [number, number];
  /** What the drawing shows, in degrees: west, south, east, north. */
  readonly bounds: readonly [number, number, number, number];
};

const WIDTH = 400;
const MIN_HEIGHT = 280;
const MAX_HEIGHT = 560;
/** Room at the edges for the outermost labels and the grid's own numbers. */
const PAD = { top: 36, right: 24, bottom: 36, left: 24 } as const;
const KM_PER_DEGREE = 111.32;
/** The label's size on the drawing, and a generous width per character, so
 *  a label is judged too wide sooner rather than drawn off the edge. */
const LABEL_SIZE = 13;
const CHAR_WIDTH = 7.2;

/**
 * Lay out a map of the named points.
 *
 * Null when there is nothing to draw — no position at all — so the page can
 * leave the map out rather than draw an empty graticule. **One point gets a
 * small area round it** rather than a map of nowhere, since a report that
 * does not divide its area is one region and still somewhere.
 */
export function layoutMap(
  named: readonly { readonly name: string; readonly position: AreaPosition | null }[],
): MapLayout | null {
  const located = named.flatMap((point) =>
    point.position === null ? [] : [{ name: point.name, ...point.position }],
  );
  if (located.length === 0) return null;

  let south = Math.min(...located.map((p) => p.lat));
  let north = Math.max(...located.map((p) => p.lat));
  let west = Math.min(...located.map((p) => p.lng));
  let east = Math.max(...located.map((p) => p.lng));

  // Never less than about thirty kilometres across, so one point, or two that
  // sit in neighbouring villages, still get a map with some room round them.
  const minSpan = 0.3;
  if (north - south < minSpan) {
    const mid = (north + south) / 2;
    south = mid - minSpan / 2;
    north = mid + minSpan / 2;
  }
  const squeeze = Math.cos((((north + south) / 2) * Math.PI) / 180);
  if ((east - west) * squeeze < minSpan) {
    const mid = (east + west) / 2;
    west = mid - minSpan / squeeze / 2;
    east = mid + minSpan / squeeze / 2;
  }
  // A margin of a tenth each way, so no point sits on the frame.
  const latMargin = (north - south) * 0.1;
  const lngMargin = (east - west) * 0.1;
  south -= latMargin;
  north += latMargin;
  west -= lngMargin;
  east += lngMargin;

  const innerWidth = WIDTH - PAD.left - PAD.right;
  const pxPerDegreeLng = innerWidth / (east - west);
  const pxPerDegreeLat = pxPerDegreeLng / squeeze;
  const rawHeight = (north - south) * pxPerDegreeLat + PAD.top + PAD.bottom;
  const height = Math.round(Math.min(MAX_HEIGHT, Math.max(MIN_HEIGHT, rawHeight)));

  // If the height was clamped, the scale has to follow so north stays up and
  // proportions stay true: fit whichever way is tighter, and centre the rest.
  const innerHeight = height - PAD.top - PAD.bottom;
  const scale = Math.min(
    innerWidth / ((east - west) * squeeze),
    innerHeight / (north - south),
  ); // px per degree of latitude
  const offsetX = PAD.left + (innerWidth - (east - west) * squeeze * scale) / 2;
  const offsetY = PAD.top + (innerHeight - (north - south) * scale) / 2;
  const x = (lng: number) => offsetX + (lng - west) * squeeze * scale;
  const y = (lat: number) => offsetY + (north - lat) * scale;

  const points = placeLabels(
    located.map((p) => ({ x: x(p.lng), y: y(p.lat), label: p.name })),
  );

  const latStep = niceStep(north - south);
  const lngStep = niceStep(east - west);
  const latitudes = ticks(south, north, latStep).map((lat) => ({
    at: y(lat),
    label: `${formatDegree(lat, latStep)}° N`,
  }));
  const longitudes = ticks(west, east, lngStep).map((lng) => ({
    at: x(lng),
    label: `${formatDegree(Math.abs(lng), lngStep)}° ${lng < 0 ? "V" : "Ö"}`,
  }));

  const kmPerPixel = KM_PER_DEGREE / scale;
  const km = niceKm(kmPerPixel * WIDTH * 0.22);

  return {
    width: WIDTH,
    height,
    points,
    latitudes,
    longitudes,
    scale: { km, length: km / kmPerPixel },
    project: (lng, lat) => [x(lng), y(lat)],
    bounds: [
      west - offsetX / (squeeze * scale),
      north - (height - offsetY) / scale,
      west + (WIDTH - offsetX) / (squeeze * scale),
      north + offsetY / scale,
    ],
  };
}

/**
 * Labels beside their points, turned to the other side where they would run
 * off the drawing or into one already placed.
 *
 * Greedy and good enough: a wine area has at most eight regions, and the
 * order is the report's — most important first — so the region a reader
 * looks for first keeps the natural place. Four places are tried, right,
 * left, then the same a line lower; if none is free the first is used, and
 * two labels touch rather than one going missing.
 */
export function placeLabels(
  points: readonly { readonly x: number; readonly y: number; readonly label: string }[],
): PlacedPoint[] {
  type Box = { left: number; right: number; top: number; bottom: number };
  const taken: Box[] = points.map((p) => ({
    left: p.x - 7,
    right: p.x + 7,
    top: p.y - 7,
    bottom: p.y + 7,
  }));
  const overlaps = (a: Box, b: Box) =>
    a.left < b.right && b.left < a.right && a.top < b.bottom && b.top < a.bottom;

  return points.map((point) => {
    const width = point.label.length * CHAR_WIDTH;
    const candidates: { anchor: "start" | "end"; dy: number }[] = [
      { anchor: "start", dy: 0 },
      { anchor: "end", dy: 0 },
      { anchor: "start", dy: LABEL_SIZE + 2 },
      { anchor: "end", dy: LABEL_SIZE + 2 },
    ];
    const boxOf = (anchor: "start" | "end", dy: number): Box => {
      const left = anchor === "start" ? point.x + 11 : point.x - 11 - width;
      const baseline = point.y + 4 + dy;
      return {
        left,
        right: left + width,
        top: baseline - LABEL_SIZE,
        bottom: baseline + 3,
      };
    };
    const fits = (box: Box) =>
      box.left >= 2 &&
      box.right <= WIDTH - 2 &&
      !taken.some((other) => overlaps(box, other));
    const chosen =
      candidates.find(({ anchor, dy }) => fits(boxOf(anchor, dy))) ??
      // Nothing free: keep it on the drawing, touching rather than missing.
      (point.x + 11 + width <= WIDTH - 2
        ? { anchor: "start" as const, dy: 0 }
        : { anchor: "end" as const, dy: 0 });
    taken.push(boxOf(chosen.anchor, chosen.dy));
    return {
      x: point.x,
      y: point.y,
      label: point.label,
      anchor: chosen.anchor,
      labelX: chosen.anchor === "start" ? point.x + 11 : point.x - 11,
      labelY: point.y + 4 + chosen.dy,
    };
  });
}

/** A step of degrees that gives about three lines across the span. */
export function niceStep(span: number): number {
  const steps = [0.05, 0.1, 0.2, 0.25, 0.5, 1, 2, 5, 10];
  return steps.find((step) => span / step <= 4) ?? 10;
}

/** A round number of kilometres near `near`. */
export function niceKm(near: number): number {
  const kms = [1, 2, 5, 10, 20, 25, 50, 100, 200, 500];
  return kms.reduce((best, km) =>
    Math.abs(km - near) < Math.abs(best - near) ? km : best,
  );
}

function ticks(from: number, to: number, step: number): number[] {
  const out: number[] = [];
  for (let value = Math.ceil(from / step) * step; value <= to; value += step) {
    out.push(Math.round(value / step) * step);
  }
  return out;
}

/** Decimal comma, and as many decimals as the step needs. */
function formatDegree(value: number, step: number): string {
  const decimals = step >= 1 ? 0 : step >= 0.1 && Number.isInteger(step * 10) ? 1 : 2;
  return value.toFixed(decimals).replace(".", ",");
}
